import { LocalDate, LocalDateTime, LocalTime, ZonedDateTime, ZoneId } from "@js-joda/core";
import { JsonApiError } from "@jsonapi-serde/client";
import { Big } from "big.js";
import { PhoneNumber } from "libphonenumber-js";
import { enqueueSnackbar } from "notistack";

export const apiUrl = (path: string): URL => new URL(path, import.meta.env.VITE_APP_API_ENDPOINT);

export const getErrorMessage = (error: unknown): string => {
    console.error(error);

    if (!(error instanceof JsonApiError)) {
        return "An unknown error occurred";
    }

    const jsonApiError = error.errors[0];

    return jsonApiError.detail ?? jsonApiError.title ?? "An unknown error occurred";
};

export const defaultMutationErrorHandler = (error: unknown) => {
    enqueueSnackbar(getErrorMessage(error), { variant: "error" });
};

const isJsJodaObject = (
    object: unknown,
): object is LocalDate | LocalTime | LocalDateTime | ZonedDateTime | ZoneId => {
    return (
        object instanceof LocalDate ||
        object instanceof LocalTime ||
        object instanceof LocalDateTime ||
        object instanceof ZonedDateTime ||
        object instanceof ZoneId
    );
};

// query-core keeps these internal, so they are copied from it rather than imported.
const hasObjectPrototype = (value: unknown): value is object =>
    Object.prototype.toString.call(value) === "[object Object]";

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    if (!hasObjectPrototype(value)) {
        return false;
    }

    const objectPrototype: unknown = Object.getPrototypeOf(value);

    if (objectPrototype === null) {
        return true;
    }

    const prototypeConstructor = (objectPrototype as { constructor?: unknown }).constructor;

    if (prototypeConstructor === undefined) {
        return true;
    }

    if (typeof prototypeConstructor !== "function") {
        return false;
    }

    const { prototype } = prototypeConstructor as { prototype: unknown };

    return (
        hasObjectPrototype(prototype) &&
        Object.hasOwn(prototype, "isPrototypeOf") &&
        objectPrototype === Object.prototype
    );
};

const isPlainArray = (value: unknown): value is unknown[] =>
    Array.isArray(value) && value.length === Object.keys(value).length;

const areSetsEquals = (a: Set<unknown>, b: Set<unknown>): boolean => {
    if (a.size !== b.size) {
        return false;
    }

    for (const value of a) {
        if (b.has(value)) {
            continue;
        }

        // Set.has compares by reference, so a set of the value objects handled below would
        // never share even when its members are equal.
        if (![...b].some((other) => areValueObjectsEqual(value, other) === true)) {
            return false;
        }
    }

    return true;
};

const replaceItem = (oldItem: unknown, newItem: unknown, depth: number): unknown => {
    if (
        oldItem === null ||
        newItem === null ||
        typeof oldItem !== "object" ||
        typeof newItem !== "object"
    ) {
        return newItem;
    }

    return extendedReplaceEqualDeep(oldItem, newItem, depth + 1);
};

const replaceArrayItems = (oldData: unknown[], newData: unknown[], depth: number): unknown[] => {
    const oldSize = oldData.length;
    const copy = new Array<unknown>(newData.length);
    let equalItems = 0;

    for (const [index, newItem] of newData.entries()) {
        const oldItem = oldData[index];
        copy[index] = oldItem === newItem ? oldItem : replaceItem(oldItem, newItem, depth);

        if (copy[index] === oldItem && index < oldSize) {
            equalItems += 1;
        }
    }

    return oldSize === newData.length && equalItems === oldSize ? oldData : copy;
};

const replaceObjectProperties = (
    oldData: Record<string, unknown>,
    newData: Record<string, unknown>,
    depth: number,
): Record<string, unknown> => {
    const oldSize = Object.keys(oldData).length;
    const newKeys = Object.keys(newData);
    const copy: Record<string, unknown> = {};
    let equalItems = 0;

    for (const key of newKeys) {
        const oldItem = oldData[key];
        const newItem = newData[key];
        copy[key] = oldItem === newItem ? oldItem : replaceItem(oldItem, newItem, depth);

        if (copy[key] === oldItem && Object.hasOwn(oldData, key)) {
            equalItems += 1;
        }
    }

    return oldSize === newKeys.length && equalItems === oldSize ? oldData : copy;
};

// Returns undefined when the pair is not a value object, so the caller falls through
// to the array and object branches.
const areValueObjectsEqual = (oldData: unknown, newData: unknown): boolean | undefined => {
    if (oldData instanceof Set && newData instanceof Set) {
        return areSetsEquals(oldData, newData);
    }

    if (isJsJodaObject(oldData) && isJsJodaObject(newData)) {
        return oldData.equals(newData);
    }

    if (oldData instanceof Big && newData instanceof Big) {
        return oldData.eq(newData);
    }

    if (oldData instanceof PhoneNumber && newData instanceof PhoneNumber) {
        return oldData.isEqual(newData);
    }

    return undefined;
};

const maxDepth = 500;

// Mirrors replaceEqualDeep from @tanstack/query-core, which is not extensible, with the
// non-JSON values this app caches settled before the plain array and object branches.
export const extendedReplaceEqualDeep = (
    oldData: unknown,
    newData: unknown,
    depth = 0,
): unknown => {
    if (oldData === newData) {
        return oldData;
    }

    if (depth > maxDepth) {
        return newData;
    }

    const valuesEqual = areValueObjectsEqual(oldData, newData);

    if (valuesEqual !== undefined) {
        return valuesEqual ? oldData : newData;
    }

    if (isPlainArray(oldData) && isPlainArray(newData)) {
        return replaceArrayItems(oldData, newData, depth);
    }

    if (isPlainObject(oldData) && isPlainObject(newData)) {
        return replaceObjectProperties(oldData, newData, depth);
    }

    return newData;
};
